/**
 * DIN EN 50160 voltage limits for low-voltage networks. Legally fixed — never
 * user-configurable. Mirrors backend VoltageConstants.cs.
 *
 * The limits are defined in volts relative to Un = 230 V (the norm's numbers);
 * the pu values are DERIVED on the load-flow base 400 V/√3 = 230,94 V and are
 * therefore slightly asymmetric around 1,0 pu (0,8963 / 1,0955). Compare pu
 * values against these constants, never against 0.9 / 1.1 literals.
 */
import { NOMINAL_VOLTAGE_LN_V, PU_BASE_LN_V } from './electricalConstants';

/** Operational band half-width as fraction of Un (±10 %, 95 % of the weekly 10-min values). */
export const VOLTAGE_OPERATIONAL_BAND_FRACTION = 0.10;

/** Warning margin as fraction of Un — yellow when within 3 % of a limit (operational, not normative). */
export const VOLTAGE_WARNING_MARGIN_FRACTION = 0.03;

/** Absolute lower limit below Un (100 % rule: −15 %). */
export const VOLTAGE_ABSOLUTE_LOWER_FRACTION = 0.15;

// ── Limits in volts (L-N) ─────────────────────────────────────────────────

/** 207 V — lower limit of the operational band. */
export const VOLTAGE_OPERATIONAL_MIN_V = NOMINAL_VOLTAGE_LN_V * (1 - VOLTAGE_OPERATIONAL_BAND_FRACTION);

/** 253 V — upper limit of the operational band. */
export const VOLTAGE_OPERATIONAL_MAX_V = NOMINAL_VOLTAGE_LN_V * (1 + VOLTAGE_OPERATIONAL_BAND_FRACTION);

/** 195,5 V — absolute lower limit (100 % rule, must never be breached). */
export const VOLTAGE_ABSOLUTE_MIN_V = NOMINAL_VOLTAGE_LN_V * (1 - VOLTAGE_ABSOLUTE_LOWER_FRACTION);

/** 253 V — absolute upper limit (identical to the operational limit). */
export const VOLTAGE_ABSOLUTE_MAX_V = VOLTAGE_OPERATIONAL_MAX_V;

/** 6,9 V — warning margin (3 % of Un). */
export const VOLTAGE_WARNING_MARGIN_V = NOMINAL_VOLTAGE_LN_V * VOLTAGE_WARNING_MARGIN_FRACTION;

/** 213,9 V — warning low threshold. */
export const VOLTAGE_WARNING_LOW_V = VOLTAGE_OPERATIONAL_MIN_V + VOLTAGE_WARNING_MARGIN_V;

/** 246,1 V — warning high threshold. */
export const VOLTAGE_WARNING_HIGH_V = VOLTAGE_OPERATIONAL_MAX_V - VOLTAGE_WARNING_MARGIN_V;

// ── The same limits in load-flow pu (derived) ─────────────────────────────

/** 207 V in load-flow pu ≈ 0,8963 — Track 1: real-time alerting. */
export const VOLTAGE_OPERATIONAL_MIN_PU = VOLTAGE_OPERATIONAL_MIN_V / PU_BASE_LN_V;

/** 253 V in load-flow pu ≈ 1,0955. */
export const VOLTAGE_OPERATIONAL_MAX_PU = VOLTAGE_OPERATIONAL_MAX_V / PU_BASE_LN_V;

/** 6,9 V in load-flow pu ≈ 0,0299. */
export const VOLTAGE_WARNING_MARGIN_PU = VOLTAGE_WARNING_MARGIN_V / PU_BASE_LN_V;

/** 195,5 V in load-flow pu ≈ 0,8466 — Track 2: EN 50160 100 % rule. */
export const VOLTAGE_ABSOLUTE_MIN_PU = VOLTAGE_ABSOLUTE_MIN_V / PU_BASE_LN_V;

/** 253 V in load-flow pu ≈ 1,0955 — Track 2: EN 50160 100 % rule. */
export const VOLTAGE_ABSOLUTE_MAX_PU = VOLTAGE_ABSOLUTE_MAX_V / PU_BASE_LN_V;

/** Derived: warning low threshold in pu (≈ 0,9262). */
export const VOLTAGE_WARNING_LOW_PU = VOLTAGE_WARNING_LOW_V / PU_BASE_LN_V;

/** Derived: warning high threshold in pu (≈ 1,0656). */
export const VOLTAGE_WARNING_HIGH_PU = VOLTAGE_WARNING_HIGH_V / PU_BASE_LN_V;

// ── Unbalance and the weekly rule ─────────────────────────────────────────

/** Voltage unbalance limit (EN 50160 4.2.5: VUF ≤ 2 % in 95 % of the weekly 10-min values). */
export const VOLTAGE_UNBALANCE_LIMIT_PCT = 2;

/** The "95 % rule": share of weekly 10-min values that must lie in band / below the unbalance limit. */
export const VOLTAGE_WEEKLY_COMPLIANCE_PCT = 95;

// ── Labels derived from the constants (never restate the numbers in JSX) ──

const fmtV = (v: number) => v.toLocaleString('de-DE', { maximumFractionDigits: 1 });

/** "207–253 V" */
export const VOLTAGE_BAND_LABEL = `${fmtV(VOLTAGE_OPERATIONAL_MIN_V)}–${fmtV(VOLTAGE_OPERATIONAL_MAX_V)} V`;

/** "±10 %" */
export const VOLTAGE_BAND_PCT_LABEL = `±${Math.round(VOLTAGE_OPERATIONAL_BAND_FRACTION * 100)} %`;

/** "195,5–253 V" */
export const VOLTAGE_ABSOLUTE_BAND_LABEL = `${fmtV(VOLTAGE_ABSOLUTE_MIN_V)}–${fmtV(VOLTAGE_ABSOLUTE_MAX_V)} V`;

/** "0,896–1,096 p.u." — the band on the load-flow base, for texts that talk pu. */
export const VOLTAGE_BAND_PU_LABEL = `${VOLTAGE_OPERATIONAL_MIN_PU.toLocaleString('de-DE', { maximumFractionDigits: 3 })}–${VOLTAGE_OPERATIONAL_MAX_PU.toLocaleString('de-DE', { maximumFractionDigits: 3 })} p.u.`;
